import React, { useMemo, useState } from 'react';
import {
    Card,
    Typography,
    CardMedia,
    CardContent,
    Link,
    Box,
} from '@mui/material';
import { getGalleryImageUrl } from 'api/utils';
import PlaceIcon from '@mui/icons-material/Place';
import Carousel from 'react-material-ui-carousel';
import * as Icons from '@mui/icons-material';
import { getTheme } from 'config/theme';
import StackedAmenities from './stackedAmenities';
import WeeklyDiscountBubble from './weeklyDiscountBubble';
import CommunityPricingBubble from './communityPricingBubble';
import WeekGrid from './weekGrid';
import dayjs from 'dayjs';

const getIconComponent = (iconName) => {
    return Icons[iconName] || Icons.Help; // Default to HelpIcon if not found
};

const SpaceCard = ({
    space,
    selectedDayIndex,
    priceDisplayMode = 'best',
    ...props
}) => {
    const [theme] = useState(getTheme(null));
    const googleMapsLink = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(space.venue_address)}`;

    // Calculate the display price based on selected day or best price of the week
    const { displayPrice, priceLabel, isAvailable, originalPrice, dateLabel } = useMemo(() => {
        const prices = space.item_price || [];
        const origPrices = space.original_price || [];
        
        if (selectedDayIndex !== null && selectedDayIndex !== undefined) {
            // Day selected: show that day's price
            const price = prices[selectedDayIndex];
            const origPrice = origPrices[selectedDayIndex];
            
            // Calculate the date for the selected day
            const selectedDate = dayjs(props.startDate).add(selectedDayIndex, 'day');
            const formattedDate = selectedDate.format('ddd D MMM');
            
            if (price === null || price === undefined || price === '-') {
                return { 
                    displayPrice: null, 
                    priceLabel: 'N/A', 
                    isAvailable: false,
                    originalPrice: null,
                    dateLabel: formattedDate
                };
            }
            return { 
                displayPrice: price, 
                priceLabel: null, 
                isAvailable: true,
                originalPrice: origPrice !== price ? origPrice : null,
                dateLabel: formattedDate
            };
        } else {
            // No day selected: show best (lowest) price of the week
            const validPrices = prices.filter(p => p !== null && p !== undefined && typeof p === 'number');
            
            // Format date range
            const startFormatted = dayjs(props.startDate).format('D MMM');
            const endFormatted = dayjs(props.endDate).format('D MMM');
            
            if (validPrices.length === 0) {
                return { 
                    displayPrice: null, 
                    priceLabel: 'N/A', 
                    isAvailable: false,
                    originalPrice: null,
                    dateLabel: `${startFormatted} - ${endFormatted}`
                };
            }
            
            const bestPrice = Math.min(...validPrices);
            return { 
                displayPrice: bestPrice, 
                priceLabel: 'Best Price', 
                isAvailable: true,
                originalPrice: null,
                dateLabel: `${startFormatted} - ${endFormatted}`
            };
        }
    }, [space.item_price, space.original_price, selectedDayIndex, props.startDate, props.endDate]);

    const dateRangeWithPrices = useMemo(() => {
        if (!props.startDate || !props.endDate || !space.item_price) return [];

        const dates = [];
        const currentDate = new Date(props.startDate);
        const end = new Date(props.endDate);
        let priceIndex = 0;

        while (currentDate <= end) {
            dates.push({
                date: new Date(currentDate),
                price: space.item_price[priceIndex] || '-',
                original_price: space?.original_price[priceIndex] || '-',
                minimum_price: space?.minimum_price[priceIndex] || '-',
                dayOfWeek: currentDate.toLocaleDateString('en-US', { weekday: 'short' })
            });

            // Move to next date and potentially next price
            currentDate.setDate(currentDate.getDate() + 1);
            priceIndex = Math.min(priceIndex + 1, space.item_price.length - 1);
        }

        return dates;
    }, [props.startDate, props.endDate, space.item_price]);

    const getPriceColor = useMemo(() => {
        if (dateRangeWithPrices.length === 0) return () => 'blue.sad';

        // Same scheme as the venue detail calendar (compactCardList):
        // - orange (primary.main)  → price equals the API minimum_price (cheapest this space gets)
        // - green  (green.sad)     → price is discounted vs original but not at minimum
        // - blue   (blue.sad)      → regular / full price
        // Pre-compute the cheapest price across the whole displayed week
        const weekPrices = dateRangeWithPrices
            .filter(d => typeof d.price === 'number')
            .map(d => d.price);
        const weekMin = Math.min(...weekPrices);
        const weekMax = Math.max(...weekPrices);
        const hasVariation = weekMin < weekMax; // only meaningful if prices differ

        return (price, originalPrice, minimumPrice) => {
            if (typeof price !== 'number') return 'blue.sad';
            // Orange: genuinely the cheapest day (there must be variation)
            if (hasVariation && price === weekMin) return 'primary.main';
            // Green: discounted vs original price but not the cheapest day
            if (typeof originalPrice === 'number' && price < originalPrice) return 'green.sad';
            // Blue: regular price
            return 'blue.sad';
        };
    }, [dateRangeWithPrices]);

    return (
        <Card
            ref={(el) => (props.listItemRefs.current[space.slug] = el)}
            sx={{
                maxHeight: '460px',
                display: 'flex',
                flexDirection: 'column',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: props.isSelected 
                    ? '0 8px 30px rgba(0, 0, 0, 0.12)' 
                    : '0 2px 8px rgba(0, 0, 0, 0.08)',
                border: props.isSelected 
                    ? `2px solid ${theme.palette.primary.main}` 
                    : '1px solid rgba(0, 0, 0, 0.06)',
                borderRadius: '16px',
                overflow: 'hidden',
                backgroundColor: '#ffffff',
                '&:hover': {
                    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
                    transform: 'translateY(-6px)',
                    borderColor: 'rgba(0, 0, 0, 0.1)'
                }
            }}
            onMouseEnter={() => props.onMouseEnter?.(space.slug)}
            onMouseLeave={() => props.onMouseLeave?.(space.slug)}
        >
            {/* Gallery Section */}
            <Box sx={{
                position: 'relative',
                height: '200px', // Fixed height for gallery
            }}>
                <WeeklyDiscountBubble 
                    dateRangeWithPrices={dateRangeWithPrices}
                    minDiscountThreshold={10} // Only show if discount is at least 10%
                />
                <CommunityPricingBubble community={space.applied_community} />
                <StackedAmenities 
                    space={space} 
                    getIconComponent={getIconComponent} 
                    theme={theme} 
                    maxDisplay={3} 
                    />
                <Carousel
                    autoPlay={false}
                    animation="slide"
                    indicators={true}
                    navButtonsAlwaysVisible
                    sx={{
                        height: '100%',
                        '& .MuiPaper-root': { borderRadius: 0 },
                        '& .MuiButtonBase-root': {
                            color: 'white',
                            backgroundColor: 'rgba(0,0,0,0.3)',
                            backdropFilter: 'blur(4px)',
                            '&:hover': {
                                backgroundColor: 'rgba(0,0,0,0.5)'
                            },
                            zIndex: 9,
                            transition: 'all 0.2s ease',
                        },
                        '& .Carousel-indicators-8': {
                            marginTop: '-32px',
                        },
                        '& button.Mui-selected': {
                            backgroundColor: 'white',
                        }
                    }}
                >
                    {(space.venue_gallery || [{ image: space.image }]).map((item, i) => (
                        <CardMedia
                            key={i}
                            component="img"
                            height="200"
                            image={getGalleryImageUrl(item, null)}
                            alt={space.venue_name}
                            sx={{
                                objectFit: 'cover',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    transform: 'scale(1.02)',
                                }
                            }}
                            onClick={() => props.setSelectedSpace(space)}
                        />
                    ))}
                </Carousel>
            </Box>

            <CardContent sx={{
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                p: 2,
                pt: 1.5,
                '&:last-child': { pb: 2 }
            }}>
                {/* Venue Name */}
                <Typography
                    variant="subtitle1"
                    sx={{
                        fontWeight: 700,
                        color: theme.palette.text.primary,
                        mb: 0.5,
                        lineHeight: 1.3,
                        fontSize: '1.05rem',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        transition: 'color 0.2s ease',
                        '&:hover': {
                            color: theme.palette.primary.main
                        }
                    }}
                    onClick={() => props.setSelectedSpace(space)}
                >
                    {space.venue_name}
                </Typography>

                {/* Address */}
                <Link
                    href={googleMapsLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        color: 'text.secondary',
                        textDecoration: 'none',
                        mb: 0,
                        fontSize: '0.75rem',
                        transition: 'color 0.2s ease',
                        '&:hover': { 
                            color: theme.palette.primary.main,
                            textDecoration: 'none'
                        }
                    }}
                >
                    <PlaceIcon sx={{ fontSize: '0.95rem', mr: 0.5, opacity: 0.7 }} />
                    <Typography variant="body2" noWrap sx={{
                        fontSize: '0.85rem',
                        fontWeight: 400,
                        letterSpacing: '0.01em'
                    }}>
                        {space.venue_address}
                    </Typography>
                </Link>
            </CardContent>
            
            {/* Price Section */}
            <Box sx={{
                px: 2,
                py: 1.5,
                borderTop: '1px solid rgba(0, 0, 0, 0.06)',
                backgroundColor: isAvailable ? '#fafafa' : '#f5f5f5',
            }}>
                {/* Week mode: show Mon-Fri price grid */}
                {priceDisplayMode === 'week' && (selectedDayIndex === null || selectedDayIndex === undefined) ? (
                    <Box>
                        <WeekGrid
                            dateRangeWithPrices={dateRangeWithPrices}
                            onDayClick={props.onDetailClick}
                            space={space}
                            getPriceColor={getPriceColor}
                            variant="compact"
                            maxWidth="100%"
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                            <Box
                                component="button"
                                onClick={() => props.setSelectedSpace(space)}
                                sx={{
                                    px: 2,
                                    py: 0.75,
                                    borderRadius: '8px',
                                    border: 'none',
                                    backgroundColor: theme.palette.primary.main,
                                    color: 'white',
                                    fontSize: '0.85rem',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        backgroundColor: theme.palette.primary.dark,
                                        transform: 'scale(1.02)',
                                    }
                                }}
                            >
                                View
                            </Box>
                        </Box>
                    </Box>
                ) : (
                    /* Default / single-day mode */
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            {/* Date Label */}
                            <Typography sx={{
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                color: theme.palette.primary.main,
                                mb: 0.25,
                            }}>
                                {dateLabel}
                            </Typography>
                            {priceLabel && (
                                <Typography sx={{
                                    fontSize: '0.65rem',
                                    fontWeight: 500,
                                    color: '#737373',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                }}>
                                    {priceLabel}
                                </Typography>
                            )}
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                {originalPrice && (
                                    <Typography sx={{
                                        fontSize: '0.85rem',
                                        fontWeight: 500,
                                        color: '#a3a3a3',
                                        textDecoration: 'line-through',
                                    }}>
                                        £{originalPrice.toFixed(0)}
                                    </Typography>
                                )}
                                <Typography sx={{
                                    fontSize: isAvailable ? '1.25rem' : '1rem',
                                    fontWeight: 700,
                                    color: isAvailable 
                                        ? (originalPrice ? theme.palette.success.main : theme.palette.primary.main)
                                        : '#a3a3a3',
                                }}>
                                    {isAvailable ? `£${displayPrice.toFixed(0)}` : 'N/A'}
                                </Typography>
                            </Box>
                        </Box>
                        
                        <Box
                            component="button"
                            onClick={() => props.setSelectedSpace(space)}
                            sx={{
                                px: 2,
                                py: 1,
                                borderRadius: '8px',
                                border: 'none',
                                backgroundColor: isAvailable ? theme.palette.primary.main : '#e5e5e5',
                                color: isAvailable ? 'white' : '#737373',
                                fontSize: '0.85rem',
                                fontWeight: 600,
                                cursor: isAvailable ? 'pointer' : 'default',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    backgroundColor: isAvailable ? theme.palette.primary.dark : '#e5e5e5',
                                    transform: isAvailable ? 'scale(1.02)' : 'none',
                                }
                            }}
                        >
                            {isAvailable ? 'View' : 'Full'}
                        </Box>
                    </Box>
                )}
            </Box>
        </Card>
    );
};

export default SpaceCard;